import { Box, Typography, useTheme } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import { Skeleton } from "../../shared";
import { getUserInfo } from "../../../services/me";
import { fetchExchangeRate } from "../../../services/exchange-rate";
import { useDesktopLayout } from "../../../hooks";
import { v } from "../../../utils/runtime";

interface WalletBalanceBlockProps {
  isLoading?: boolean;
}

function formatAmount(val: number, digits = 2): string {
  return val.toLocaleString("ru-RU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
}

/** Ключ для AnimatePresence — рендерим новый span только если поменялось
 *  видимое значение (иначе будем мигать при каждом ре-фетче с той же ценой). */
function rateKey(v: number): string {
  return v.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

export function WalletBalanceBlock({ isLoading: parentLoading }: WalletBalanceBlockProps) {
  const theme = useTheme();
  const isDesktop = useDesktopLayout();
  const walletLogoSrc =
    theme.palette.mode === "dark" ? v("/zero-logo.svg") : v("/zero-logo-l.svg");
  const { data: user, isLoading: userLoading } = useQuery({
    queryFn: getUserInfo,
    queryKey: ["me"],
  });

  const {
    data: rateData,
    isLoading: rateLoading,
    dataUpdatedAt: rateUpdatedAt,
  } = useQuery({
    queryFn: () =>
      fetchExchangeRate({
        fromCurrency: "USDT",
        toCurrency: "RUB",
        sellAmount: 1,
      }),
    queryKey: ["rate", "USDT", "RUB"],
    // Тикерное обновление раз в 5 секунд. `refetchIntervalInBackground: false`
    // экономит батарею — когда WebApp не в фокусе, синк ставится на паузу.
    refetchInterval: 5000,
    refetchIntervalInBackground: false,
    // Держим прошлое значение видимым, пока летит следующий запрос —
    // избегаем «мигания» на пустое место в кадре обновления.
    keepPreviousData: true,
  });

  const usdtBalance =
    user?.balances?.find((b) => b.currency === "USDT")?.amount ?? "0";
  const usdt = Number(usdtBalance) || 0;
  const rate = Number(rateData?.show_rate ?? rateData?.rate ?? 0);
  const rub = usdt * (rate || 0);

  // Отслеживаем направление изменения курса для короткой подсветки
  // (зелёная — рост, красная — падение). Держим ~600мс.
  const prevRateRef = useRef<number>(0);
  const [direction, setDirection] = useState<"up" | "down" | null>(null);
  useEffect(() => {
    const prev = prevRateRef.current;
    if (prev > 0 && rate > 0 && rateKey(prev) !== rateKey(rate)) {
      setDirection(rate > prev ? "up" : "down");
      const t = setTimeout(() => setDirection(null), 700);
      prevRateRef.current = rate;
      return () => clearTimeout(t);
    }
    if (rate > 0) prevRateRef.current = rate;
  }, [rate]);

  const isLoading = parentLoading || userLoading || (rateLoading && !rateData);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "10px",
          marginTop: "10px",
        }}
      >
        <Skeleton
          variant="rounded"
          width={140}
          height={16}
          sx={{ borderRadius: "6px" }}
        />
        <Skeleton
          variant="rounded"
          width={160}
          height={34}
          sx={{ borderRadius: "10px", marginTop: "4px" }}
        />
        <Skeleton
          variant="rounded"
          width={170}
          height={24}
          sx={{ borderRadius: "12px", marginTop: "2px" }}
        />
      </Box>
    );
  }

  const flashColor =
    direction === "up"
      ? "#22c55e"
      : direction === "down"
        ? "#ef4444"
        : undefined;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "8px",
        marginTop: "10px",
      }}
    >
      {!isDesktop && (
        <Box
          component="img"
          src={walletLogoSrc}
          alt="Zero Wallet"
          sx={{
            height: "40px",
            width: "auto",
            display: "block",
            userSelect: "none",
            pointerEvents: "none",
          }}
          draggable={false}
        />
      )}

      <Typography
        sx={{
          fontSize: "32px",
          fontWeight: 700,
          lineHeight: 1.1,
          letterSpacing: "-0.5px",
        }}
      >
        {formatAmount(usdt, 2)} USDT
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginTop: "4px",
        }}
      >
        <Typography sx={{ fontSize: "14px", opacity: 0.6, fontWeight: 500 }}>
          ≈ {formatAmount(rub, 0)} RUB
        </Typography>

        <Box
          sx={(theme) => ({
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "3px 10px",
            borderRadius: "10px",
            bgcolor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.08)"
                : "rgba(0, 0, 0, 0.06)",
            border: "1px solid transparent",
            transition:
              "border-color 250ms ease-out, box-shadow 250ms ease-out",
            ...(flashColor && {
              borderColor: `${flashColor}55`,
              boxShadow: `0 0 0 3px ${flashColor}22`,
            }),
          })}
        >
          <Typography
            component="span"
            sx={{
              fontSize: "12px",
              fontWeight: 500,
              display: "inline-flex",
              alignItems: "baseline",
              gap: "3px",
              lineHeight: 1,
            }}
          >
            $1 =
            <Box
              component="span"
              sx={{
                position: "relative",
                display: "inline-block",
                minWidth: "38px",
                textAlign: "right",
                height: "1em",
                overflow: "hidden",
              }}
            >
              <AnimatePresence mode="popLayout" initial={false}>
                <motion.span
                  key={rate > 0 ? rateKey(rate) : "empty"}
                  initial={{ y: "-100%", opacity: 0 }}
                  animate={{
                    y: 0,
                    opacity: 1,
                    color: flashColor ?? "currentColor",
                  }}
                  exit={{ y: "100%", opacity: 0 }}
                  transition={{
                    y: { type: "spring", stiffness: 500, damping: 30 },
                    opacity: { duration: 0.18 },
                    color: { duration: 0.25 },
                  }}
                  style={{
                    display: "inline-block",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {rate ? formatAmount(rate, 2) : "—"}
                </motion.span>
              </AnimatePresence>
            </Box>
            ₽
          </Typography>

          {/* Мини-таймер до следующего обновления. Заполняется по часовой
              стрелке за 5 секунд, на каждом ре-фетче — рестарт (через `key`
              на SVG). */}
          <Box
            sx={{
              position: "relative",
              width: "12px",
              height: "12px",
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <Box
              component="svg"
              key={rateUpdatedAt}
              viewBox="0 0 20 20"
              sx={{
                width: "12px",
                height: "12px",
                transform: "rotate(-90deg)",
                "@keyframes walletRateTimer": {
                  from: { strokeDashoffset: 43.98 },
                  to: { strokeDashoffset: 0 },
                },
              }}
            >
              <circle
                cx="10"
                cy="10"
                r="7"
                fill="none"
                stroke="rgba(247,147,26,0.25)"
                strokeWidth="4"
              />
              <Box
                component="circle"
                cx="10"
                cy="10"
                r="7"
                fill="none"
                stroke="#F7931A"
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray="43.98"
                sx={{
                  animation: "walletRateTimer 5s linear forwards",
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
